import React from 'react';
import _ from 'lodash';

import styles from '../../UploadDocumentPage/index.module.css';

import * as api from '../../../../api';
import * as utils from '../../../../utils';
import * as logger from '../../../../logger';

interface Props extends React.PropsWithChildren {
  label: string;
  card: api.PatientInsuranceInformation.InsuranceInformationWithDetails;
}

interface State {
  showPatientInsuranceInformation: boolean;
  realPath: string | null;
}

class ReviewPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const { id, name } = props.card;

    this.state = {
      showPatientInsuranceInformation: !!id && name !== 'placeholder',
      realPath: null,
    };
  }

  async fetchAndSetUrl() {
    try {
      const { status, dataURL } =
        await api.client.PatientUpload.downloadImageFile(
          this.props.card.patientUploadId,
        );
      if (status === 200 && dataURL) {
        return this.setState({ realPath: dataURL });
      }
    } catch (err) {
      logger.error(
        new Error(
          `could not load real file URL for document with id ${this.props.card.id}`,
        ),
      );
    }
  }

  async componentDidMount() {
    if (
      !this.state.showPatientInsuranceInformation &&
      !!this.props.card.patientUploadId
    )
      await this.fetchAndSetUrl();
  }

  async componentDidUpdate(prevProps: Props) {
    const { id, name } = this.props.card;
    if (prevProps.card.id && this.props.card.id !== prevProps.card.id) {
      this.setState({
        showPatientInsuranceInformation: !!id && name !== 'placeholder',
        realPath: null,
      });
    }

    if (
      this.state.realPath === null &&
      !this.state.showPatientInsuranceInformation &&
      !!this.props.card.patientUploadId
    ) {
      return this.fetchAndSetUrl();
    }
  }

  renderInsuranceInformation() {
    const { label, children, card } = this.props;

    const {
      logoUrl,
      primaryPolicyHolderFirstName,
      primaryPolicyHolderLastName,
      groupNumber,
      memberId,
      planName,
      nameRaw,
      payor,
    } = card;

    const prettyInsuranceProviderName = formatString(nameRaw, true);
    const prettyPayor = formatString(payor, true);
    const noLogoUrlAndNoInsuranceProviderName =
      !logoUrl && !prettyInsuranceProviderName;

    const payorName =
      prettyPayor !== api.PatientInsuranceInformation.Payor.Other
        ? prettyPayor
        : null;
    const planNameOrInsuranceProviderName =
      payorName || prettyInsuranceProviderName || formatString(planName);

    const insuranceProviderImgSrc = logoUrl && utils.getPrevisitAsset(logoUrl);

    return (
      <>
        <div className={styles.Title}>{label}</div>
        <div className={styles.CardReview}>
          <div className={styles.CardReviewContainer}>
            <div className={styles.InsuranceProvider}>
              {insuranceProviderImgSrc ? (
                <div>
                  <img
                    src={insuranceProviderImgSrc}
                    alt="Logo of insurance provider"
                    height={40}
                  />
                </div>
              ) : (
                <div className={styles.InsuranceNameLabel}>
                  {planNameOrInsuranceProviderName}
                </div>
              )}
            </div>
            <div>
              {planNameOrInsuranceProviderName &&
              noLogoUrlAndNoInsuranceProviderName ? (
                <>
                  <strong>{planNameOrInsuranceProviderName}</strong>
                </>
              ) : null}
              <br />
              {!planNameOrInsuranceProviderName ? (
                <>
                  <strong>
                    {`${[
                      primaryPolicyHolderFirstName,
                      primaryPolicyHolderLastName,
                    ]
                      .filter((s) => !!s)
                      .join(' ')}`}
                  </strong>
                </>
              ) : (
                `${[primaryPolicyHolderFirstName, primaryPolicyHolderLastName]
                  .filter((s) => !!s)
                  .join(' ')}`
              )}
              <br />
              {groupNumber ? `Group #: ${groupNumber}` : null}
              <br />
              {memberId ? `ID #: ${memberId}` : null}
            </div>
          </div>
        </div>
        {children}
      </>
    );
  }

  render() {
    const { showPatientInsuranceInformation, realPath } = this.state;
    const { label, children } = this.props;

    if (showPatientInsuranceInformation) {
      return this.renderInsuranceInformation();
    }

    return (
      <>
        <div className={styles.Title}>{label}</div>
        {realPath && (
          <div className={styles.DocumentReview}>
            <img src={realPath} alt="review" />
          </div>
        )}
        {children}
      </>
    );
  }
}

export default ReviewPage;

const formatString = (
  insuranceProviderName: string | null,
  toUpper: boolean = false,
) => {
  if (!insuranceProviderName) return insuranceProviderName;

  return insuranceProviderName
    .split(/[ _]/g)
    .map((word) =>
      _.upperFirst(toUpper ? word.toUpperCase() : word.toLowerCase()),
    )
    .join(' ');
};
